import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  TextField,
  InputAdornment,
  CircularProgress,
  Avatar,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { useSelector } from "react-redux";
import { Navigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getUnApprovedUsers, approveUserApi } from "../../services/api/userApi";

export default function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const userInfo = useSelector((state) => state.userLogin?.userInfo);

  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Confirmation Modal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    type: null, // "approve" | "reject"
    user: null,
  });

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getUnApprovedUsers();
      if (res?.data?.success) {
        setUsers(res.data.users);
      }
    } catch (err) {
      console.error("Error fetching users:", err);
      toast.error(err?.response?.data?.message || "Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // Modal Handlers
  const handleOpenConfirm = (type, user) => {
    setConfirmModal({
      open: true,
      type,
      user,
    });
  };

  const handleCloseConfirm = () => {
    if (actionLoading) return; // Prevent closing while API request is in-flight
    setConfirmModal({
      open: false,
      type: null,
      user: null,
    });
  };

  const handleExecuteAction = async () => {
    const { type, user } = confirmModal;
    if (!user) return;

    setActionLoading(true);

    try {
      if (type === "approve") {
        const res = await approveUserApi(user._id);

        setUsers((prev) => prev.filter((u) => u._id !== user._id));

        toast.success(
          res?.data?.message ||
            `Approved ${user.fullName || user.name || "user"} successfully`,
        );
      } else if (type === "reject") {
        setUsers((prev) => prev.filter((u) => u._id !== user._id));
        toast.info(
          `Rejected request for ${user.fullName || user.name || "user"}`,
        );
      }
      handleCloseConfirm();
    } catch (err) {
      console.error("Error processing user action:", err);
      toast.error(err?.response?.data?.message || `Failed to ${type} user`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Strictly filter for pending users (!isApproved) and search query
  const filteredUsers = users.filter((user) => {
    const isPending = !user.isApproved;
    const userName = user.fullName || user.name || "";
    const matchesSearch =
      userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchQuery.toLowerCase());

    return isPending && matchesSearch;
  });

  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  
  if (userInfo.role !== "admin") {
    return <Navigate to='/app/home' replace />;
  }

  return (
    <Box
      sx={{
        p: { xs: 2, md: 4 },
        maxWidth: "100%",
        mx: "auto",
        width: "100%",
        height: "calc(100vh - 80px)",
        overflowY: "auto",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant='h4'
          sx={{
            fontWeight: 700,
            color: "#12243C",
            fontSize: { xs: "22px", sm: "28px" },
            fontFamily: "var(--font-sans)",
            mb: 0.5,
          }}
        >
          Pending Approvals
        </Typography>
        <Typography
          variant='body2'
          sx={{ color: "#6B7280", fontFamily: "var(--font-sans)" }}
        >
          Review and approve new account access requests for LawGPT.
        </Typography>
      </Box>

      {/* Main Container Card */}
      <Card
        elevation={0}
        sx={{
          borderRadius: "16px",
          border: "1px solid #E5E7EB",
          backgroundColor: "#FFFFFF",
          p: { xs: 2, sm: 3 },
        }}
      >
        {/* Controls Toolbar */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            mb: 3,
          }}
        >
          {/* Search Input */}
          <TextField
            size='small'
            placeholder='Search name or email...'
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position='start'>
                  <SearchIcon sx={{ color: "#9CA3AF", fontSize: "20px" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: "280px" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                backgroundColor: "#F9FAFB",
              },
            }}
          />
        </Box>

        {/* User Table with Horizontal Scroll Support */}
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: "12px",
            border: "1px solid #F3F4F6",
            overflowX: "auto",
            width: "100%",
          }}
        >
          <Table sx={{ minWidth: 650 }}>
            <TableHead
              sx={{
                backgroundColor: "#0073A6",
                "& .MuiTableCell-head": {
                  backgroundColor: "#0073A6",
                  color: "#FFFFFF",
                  fontWeight: 600,
                },
              }}
            >
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align='right'>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align='center' sx={{ py: 4 }}>
                    <CircularProgress size={28} sx={{ color: "#0073A6" }} />
                  </TableCell>
                </TableRow>
              ) : paginatedUsers.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={5}
                    align='center'
                    sx={{ py: 4, color: "#9CA3AF" }}
                  >
                    No pending approval requests.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedUsers.map((user) => {
                  const displayName =
                    user.fullName || user.name || "Unnamed User";
                  return (
                    <TableRow key={user._id} hover>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                          }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: "#E0F2FE",
                              color: "#0073A6",
                              width: 36,
                              height: 36,
                              fontSize: "14px",
                              fontWeight: 600,
                            }}
                          >
                            {displayName.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography
                            variant='body2'
                            sx={{ fontWeight: 600, color: "#111827" }}
                          >
                            {displayName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: "#4B5563", fontSize: "14px" }}>
                        {user.email}
                      </TableCell>
                      <TableCell>
                        <Chip
                          icon={
                            user.role === "admin" ? (
                              <AdminPanelSettingsOutlinedIcon
                                style={{ fontSize: 16 }}
                              />
                            ) : (
                              <PersonOutlineIcon style={{ fontSize: 16 }} />
                            )
                          }
                          label={user.role || "N/A"}
                          size='small'
                          sx={{
                            textTransform: "capitalize",
                            fontWeight: 500,
                            backgroundColor:
                              user.role === "admin" ? "#FEF3C7" : "#F3F4F6",
                            color:
                              user.role === "admin" ? "#D97706" : "#4B5563",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label='Pending'
                          size='small'
                          sx={{
                            fontWeight: 600,
                            backgroundColor: "#FEF3C7",
                            color: "#B45309",
                          }}
                        />
                      </TableCell>
                      <TableCell align='right'>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                          }}
                        >
                          <Button
                            variant='contained'
                            size='small'
                            startIcon={<CheckCircleOutlineIcon />}
                            onClick={() => handleOpenConfirm("approve", user)}
                            sx={{
                              backgroundColor: "#10B981",
                              textTransform: "none",
                              borderRadius: "6px",
                              "&:hover": { backgroundColor: "#059669" },
                            }}
                          >
                            Approve
                          </Button>
                          <Button
                            variant='outlined'
                            disabled
                            size='small'
                            color='error'
                            startIcon={<HighlightOffIcon />}
                            onClick={() => handleOpenConfirm("reject", user)}
                            sx={{
                              textTransform: "none",
                              borderRadius: "6px",
                            }}
                          >
                            Reject
                          </Button>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination Control Footer */}
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={filteredUsers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{ borderTop: "1px solid #F3F4F6", mt: 1 }}
        />
      </Card>

      {/* Confirmation Action Modal */}
      <Dialog
        open={confirmModal.open}
        onClose={handleCloseConfirm}
        PaperProps={{
          sx: { borderRadius: "12px", p: 1, maxWidth: "400px", width: "100%" },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <WarningAmberIcon
            color={confirmModal.type === "approve" ? "success" : "error"}
          />
          <Typography variant='h6' sx={{ fontWeight: 600 }}>
            {confirmModal.type === "approve"
              ? "Approve User Access"
              : "Reject / Revoke Access"}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: "#4B5563", fontSize: "14px" }}>
            Are you sure you want to{" "}
            <strong>
              {confirmModal.type === "approve" ? "approve" : "reject"}
            </strong>{" "}
            access for{" "}
            <strong>
              {confirmModal.user?.fullName ||
                confirmModal.user?.name ||
                confirmModal.user?.email}
            </strong>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseConfirm}
            disabled={actionLoading}
            sx={{ textTransform: "none", color: "#6B7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleExecuteAction}
            variant='contained'
            disabled={actionLoading}
            color={confirmModal.type === "approve" ? "success" : "error"}
            startIcon={
              actionLoading ? (
                <CircularProgress size={16} color='inherit' />
              ) : null
            }
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              bgcolor: confirmModal.type === "approve" ? "#10B981" : undefined,
            }}
          >
            {actionLoading
              ? "Processing..."
              : `Confirm ${confirmModal.type === "approve" ? "Approval" : "Rejection"}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
